import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
    FaEnvelope, FaLock, FaSignInAlt, FaShieldAlt,
    FaKey, FaTimes, FaCheckCircle, FaExclamationCircle,
    FaEye, FaEyeSlash
} from "react-icons/fa";
import { authenticationDetails, forgotPassword, resetPassword } from "../../../service/register.service";
import styles from "./LoginPage.module.css";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ username: "", password: "" });
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("sessionExpired") === "true") {
            setSessionExpiredNotice(true);
        }
    }, [location.search]);

    // Forgot password modal state
    const [forgotModalOpen, setForgotModalOpen] = useState(false);
    const [forgotStep, setForgotStep] = useState(1); // 1 = enter email, 2 = enter OTP & new pass, 3 = success
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [forgotData, setForgotData] = useState({
        email: "",
        otp: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [forgotErrors, setForgotErrors] = useState({});
    const [forgotAlert, setForgotAlert] = useState(null); // { type: 'success' | 'error', text: string }
    const [forgotSubmitting, setForgotSubmitting] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Rate limiting & security lockout states
    const [lockoutSeconds, setLockoutSeconds] = useState(0);
    const [rateLimitMessage, setRateLimitMessage] = useState(null);

    // Countdown interval for lockout timer
    useEffect(() => {
        if (lockoutSeconds <= 0) return;
        const timer = setInterval(() => {
            setLockoutSeconds((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setRateLimitMessage(null);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [lockoutSeconds]);

    const formatLockoutTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    const validate = () => {
        const e = {};
        if (!form.username.trim()) e.username = "Email / username is required";
        if (!form.password) e.password = "Password is required";
        return e;
    };

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSubmitting(true);
        try {
            const res = await authenticationDetails({
                username: form.username.trim().toLowerCase(),
                password: form.password,
            });

            const data = res?.data?.data;
            const token = data?.token;
            const role = data?.role;
            const username = data?.username || form.username.trim().toLowerCase();
            const displayName = data?.displayName || username;

            if (token) {
                localStorage.setItem("ivec_token", token);
                localStorage.setItem("ivec_user", username);
                localStorage.setItem("ivec_role", role || "ROLE_USER");
                localStorage.setItem("ivec_display_name", displayName);

                if (role === "ROLE_ADMIN") {
                    navigate("/admin");
                } else {
                    navigate("/payment");
                }
            } else {
                throw new Error("Token missing in response");
            }
        } catch (err) {
            const status = err?.response?.status;
            const resData = err?.response?.data;
            const rateLimitData = resData?.data;
            const errorType = rateLimitData?.errorType || "";
            const msg = resData?.message || "Login failed. Please check your credentials.";

            if (status === 429 || errorType === "ACCOUNT_LOCKOUT" || errorType === "IP_RATE_LIMIT") {
                // Scenario 3: Rate Limited / Account Lockout
                const retrySec = Number(rateLimitData?.retryAfterSeconds) || 600;
                setLockoutSeconds(retrySec);
                setRateLimitMessage(msg);
                const mins = Math.max(1, Math.ceil(retrySec / 60));

                Swal.fire({
                    icon: "error",
                    title: '<span style="color:#991b1b; font-size:1.35rem; font-weight:700;">Account Temporarily Locked</span>',
                    html: `
                        <div style="font-size: 0.94rem; color: #475569; line-height: 1.5; margin-top: 6px;">
                            Account <strong style="color: #0f172a;">${form.username.trim()}</strong> has exceeded the maximum allowed failed attempts.
                        </div>
                        <div style="margin-top: 14px; padding: 14px 16px; background: #fff1f2; border: 1.5px solid #fecdd3; border-radius: 10px; color: #9f1239; text-align: center;">
                            <div style="font-size: 0.85rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #be123c;">
                                Security Cool-Down Window
                            </div>
                            <div style="font-size: 1.35rem; font-weight: 800; font-family: monospace; color: #881337; margin-top: 4px;">
                                ~${mins} Minute${mins > 1 ? "s" : ""}
                            </div>
                            <div style="font-size: 0.8rem; color: #9f1239; margin-top: 4px;">
                                A live countdown timer is now active on your login page.
                            </div>
                        </div>
                    `,
                    confirmButtonText: "I Understand",
                    confirmButtonColor: "#2b53a3"
                });

            } else if (errorType === "USER_NOT_FOUND" || msg.toLowerCase().includes("no account found")) {
                // Scenario 1: User does not exist in DB
                Swal.fire({
                    icon: "error",
                    title: '<span style="color:#b91c1c; font-size:1.35rem; font-weight:700;">Account Not Found</span>',
                    html: `
                        <div style="font-size: 0.94rem; color: #475569; line-height: 1.6; margin-top: 6px;">
                            No registered delegate account exists with:
                            <div style="margin: 10px 0; padding: 9px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; font-weight: 600; color: #0f172a; word-break: break-all;">
                                ${form.username.trim()}
                            </div>
                            Please double-check the spelling of your email address, or register a new account.
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText: "Register Now",
                    cancelButtonText: "Check Email & Retry",
                    confirmButtonColor: "#2b53a3",
                    cancelButtonColor: "#64748b"
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate("/registration");
                    }
                });

            } else if (errorType === "PASSWORD_MISMATCH" || msg.toLowerCase().includes("incorrect password")) {
                // Scenario 2: User exists in DB, but password did not match
                const remaining = rateLimitData?.remainingAttempts !== undefined ? rateLimitData.remainingAttempts : 4;
                const max = rateLimitData?.maxAttempts || 5;
                const used = rateLimitData?.attemptCount || (max - remaining);
                const isCritical = remaining <= 1;

                Swal.fire({
                    icon: isCritical ? "error" : "warning",
                    title: `<span style="color:${isCritical ? "#b91c1c" : "#c2410c"}; font-size:1.35rem; font-weight:700;">Password Did Not Match</span>`,
                    html: `
                        <div style="font-size: 0.93rem; color: #475569; line-height: 1.5; margin-top: 6px;">
                            The password you entered is incorrect for <strong style="color: #0f172a;">${form.username.trim()}</strong>.
                        </div>

                        <div style="margin-top: 14px; padding: 12px 14px; background: ${isCritical ? "#fef2f2" : "#fffbeb"}; border: 1.5px solid ${isCritical ? "#fecaca" : "#fde68a"}; border-radius: 10px; text-align: left;">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 7px;">
                                <span style="font-size: 0.85rem; font-weight: 700; color: ${isCritical ? "#991b1b" : "#92400e"};">
                                    Login Attempt Status
                                </span>
                                <span style="background: ${isCritical ? "#dc2626" : "#d97706"}; color: #fff; font-size: 0.78rem; font-weight: 700; padding: 2px 9px; border-radius: 12px;">
                                    ${remaining} ${remaining === 1 ? "attempt" : "attempts"} left
                                </span>
                            </div>
                            <div style="background: #e2e8f0; height: 7px; border-radius: 4px; overflow: hidden;">
                                <div style="width: ${Math.min(100, (used / max) * 100)}%; height: 100%; background: ${isCritical ? "#dc2626" : "#d97706"}; transition: width 0.3s;"></div>
                            </div>
                            <div style="font-size: 0.78rem; color: ${isCritical ? "#b91c1c" : "#78350f"}; margin-top: 7px;">
                                ${isCritical
                            ? "⚠️ <strong>Warning:</strong> 1 more wrong attempt will lock your account for 10 minutes."
                            : `Attempt ${used} of ${max} used. Your account will lock for 10 minutes after ${max} failed attempts.`
                        }
                            </div>
                        </div>
                    `,
                    showDenyButton: true,
                    confirmButtonText: "Try Again",
                    denyButtonText: "Forgot Password?",
                    confirmButtonColor: "#2b53a3",
                    denyButtonColor: "#059669"
                }).then((result) => {
                    if (result.isDenied) {
                        setForgotData((prev) => ({ ...prev, email: form.username }));
                        setForgotStep(1);
                        setForgotAlert(null);
                        setForgotErrors({});
                        setForgotModalOpen(true);
                    }
                });

            } else if (status === 403 || errorType === "ACCOUNT_DISABLED") {
                Swal.fire({
                    icon: "warning",
                    title: '<span style="color:#92400e; font-size:1.35rem; font-weight:700;">Account Inactive</span>',
                    text: msg,
                    confirmButtonColor: "#2b53a3"
                });

            } else {
                Swal.fire({
                    icon: "error",
                    title: '<span style="color:#b91c1c; font-size:1.35rem; font-weight:700;">Sign In Error</span>',
                    text: msg,
                    confirmButtonColor: "#2b53a3"
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Forgot Password Flow
    const handleCloseForgotModal = () => {
        setForgotModalOpen(false);
        setForgotStep(1);
        setForgotAlert(null);
        setForgotErrors({});
        setForgotData({ email: "", otp: "", newPassword: "", confirmPassword: "" });
    };

    const handleForgotChange = (e) => {
        setForgotData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setForgotErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
        setForgotAlert(null);
    };

    const handleSendOtp = async (e) => {
        if (e) e.preventDefault();
        setForgotAlert(null);

        if (!forgotData.email.trim()) {
            setForgotErrors({ email: "Email or username is required" });
            return;
        }

        setForgotSubmitting(true);
        try {
            const res = await forgotPassword({ email: forgotData.email.trim().toLowerCase() });
            setForgotAlert({
                type: "success",
                text: res?.message || `A 6-digit verification code has been sent to ${forgotData.email.trim()}.`
            });
            setForgotStep(2);
            startCooldown(60);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to send verification code. Please check your email or username.";
            setForgotAlert({ type: "error", text: msg });
        } finally {
            setForgotSubmitting(false);
        }
    };

    const startCooldown = (seconds) => {
        setResendCooldown(seconds);
        const timer = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setForgotAlert(null);

        const errs = {};
        if (!forgotData.otp.trim()) errs.otp = "Verification code is required";
        if (!forgotData.newPassword) {
            errs.newPassword = "New password is required";
        } else if (forgotData.newPassword.length < 6) {
            errs.newPassword = "Password must be at least 6 characters";
        }
        if (forgotData.newPassword !== forgotData.confirmPassword) {
            errs.confirmPassword = "Passwords do not match";
        }

        if (Object.keys(errs).length > 0) {
            setForgotErrors(errs);
            return;
        }

        setForgotSubmitting(true);
        try {
            await resetPassword({
                email: forgotData.email.trim().toLowerCase(),
                otp: forgotData.otp.trim(),
                newPassword: forgotData.newPassword.trim()
            });

            // Smooth transition to in-modal success step (no ugly swal popup!)
            setForgotStep(3);
            setForm((prev) => ({ ...prev, username: forgotData.email.trim(), password: "" }));
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to reset password. Please verify the code and try again.";
            setForgotAlert({ type: "error", text: msg });
        } finally {
            setForgotSubmitting(false);
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.glow1} />
            <div className={styles.glow2} />

            <div className={styles.card}>
                {/* Header */}
                <div className={styles.cardHeader}>
                    <div className={styles.logo}>IVEC 2027</div>
                    <h1 className={styles.title}>Portal Login</h1>
                    <p className={styles.subtitle}>
                        Sign in with your registered credentials to access your dashboard
                    </p>
                </div>

                {/* Session Expired Notice Banner */}
                {sessionExpiredNotice && (
                    <div className={styles.sessionExpiredAlert}>
                        <FaExclamationCircle className={styles.sessionExpiredIcon} />
                        <div>
                            <strong>Session Expired</strong>
                            <p>Your previous session expired due to inactivity. Please sign in to resume.</p>
                        </div>
                    </div>
                )}

                {/* Security Lockout Alert Banner */}
                {lockoutSeconds > 0 && (
                    <div className={styles.lockoutAlert}>
                        <div className={styles.lockoutHeader}>
                            <FaShieldAlt className={styles.lockoutIcon} />
                            <span>Security Lockout Active</span>
                        </div>
                        <p className={styles.lockoutText}>{rateLimitMessage || "Too many failed login attempts."}</p>
                        <div className={styles.lockoutTimer}>
                            Time remaining: <span>{formatLockoutTime(lockoutSeconds)}</span>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                    {/* Username / Email */}
                    <div className={styles.fieldGroup}>
                        <label htmlFor="username" className={styles.label}>
                            Email / Username <span className={styles.required}>*</span>
                        </label>
                        <div className={`${styles.inputWrap} ${errors.username ? styles.inputError : ""}`}>
                            <span className={styles.icon}><FaEnvelope /></span>
                            <input
                                id="username"
                                name="username"
                                type="text"
                                autoComplete="username"
                                placeholder="name@example.com"
                                value={form.username}
                                onChange={handleChange}
                                className={styles.input}
                            />
                        </div>
                        {errors.username && <div className={styles.error}>{errors.username}</div>}
                    </div>

                    {/* Password with Eye Toggle */}
                    <div className={styles.fieldGroup}>
                        <label htmlFor="password" className={styles.label}>
                            Password <span className={styles.required}>*</span>
                        </label>
                        <div className={`${styles.inputWrap} ${errors.password ? styles.inputError : ""}`}>
                            <span className={styles.icon}><FaLock /></span>
                            <input
                                id="password"
                                name="password"
                                type={showLoginPassword ? "text" : "password"}
                                autoComplete="current-password"
                                placeholder="Enter your password"
                                value={form.password}
                                onChange={handleChange}
                                className={styles.input}
                            />
                            <button
                                type="button"
                                className={styles.toggleEye}
                                onClick={() => setShowLoginPassword((prev) => !prev)}
                                aria-label="Toggle password visibility"
                            >
                                {showLoginPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {errors.password && <div className={styles.error}>{errors.password}</div>}
                    </div>

                    {/* Forgot Password Link */}
                    <div className={styles.forgotWrap}>
                        <button
                            type="button"
                            className={styles.forgotBtn}
                            onClick={() => {
                                setForgotData((prev) => ({ ...prev, email: form.username }));
                                setForgotStep(1);
                                setForgotAlert(null);
                                setForgotErrors({});
                                setForgotModalOpen(true);
                            }}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <button
                        type="submit"
                        className={styles.submitBtn}
                        disabled={submitting || lockoutSeconds > 0}
                        id="login-submit-btn"
                    >
                        <FaSignInAlt />
                        <span>
                            {lockoutSeconds > 0
                                ? `Locked (${formatLockoutTime(lockoutSeconds)})`
                                : submitting
                                    ? "Signing in…"
                                    : "Sign In"}
                        </span>
                    </button>
                </form>

                {/* Security note */}
                <div className={styles.securityNote}>
                    <FaShieldAlt />
                    {/* <span>Your session and credentials are encrypted end-to-end</span> */}
                    <span>Your session details are handled securely</span>
                </div>

                {/* Link back */}
                <div className={styles.registerLink}>
                    Not registered yet?{" "}
                    <a href="/registration" className={styles.link}>
                        Register here
                    </a>
                </div>
            </div>

            {/* Forgot Password Modal (No awkward Swal over modal!) */}
            {forgotModalOpen && (
                <div
                    className={styles.modalOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="forgot-password-modal-title"
                >
                    <div className={styles.modalCard}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle} id="forgot-password-modal-title">
                                <FaKey style={{ marginRight: "8px" }} />
                                {forgotStep === 1 && "Forgot Password"}
                                {forgotStep === 2 && "Enter Verification Code"}
                                {forgotStep === 3 && "Password Reset Complete"}
                            </h3>
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={handleCloseForgotModal}
                                aria-label="Close modal"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            {/* In-Modal Alert Banner */}
                            {forgotAlert && (
                                <div className={`${styles.modalAlertBanner} ${forgotAlert.type === "success" ? styles.modalAlertSuccess : styles.modalAlertError}`}>
                                    {forgotAlert.type === "success" ? (
                                        <FaCheckCircle className={styles.alertIcon} />
                                    ) : (
                                        <FaExclamationCircle className={styles.alertIcon} />
                                    )}
                                    <span>{forgotAlert.text}</span>
                                </div>
                            )}

                            {/* Step 1: Request Code */}
                            {forgotStep === 1 && (
                                <form onSubmit={handleSendOtp} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                                    <p className={styles.modalSubtitle}>
                                        Enter your registered email address or username. We will send you a 6-digit verification code to reset your password.
                                    </p>
                                    <div className={styles.fieldGroup}>
                                        <label className={styles.label}>
                                            Email / Username <span className={styles.required}>*</span>
                                        </label>
                                        <div className={`${styles.inputWrap} ${forgotErrors.email ? styles.inputError : ""}`}>
                                            <span className={styles.icon}><FaEnvelope /></span>
                                            <input
                                                type="text"
                                                name="email"
                                                value={forgotData.email}
                                                onChange={handleForgotChange}
                                                placeholder="name@example.com"
                                                className={styles.input}
                                                autoFocus
                                            />
                                        </div>
                                        {forgotErrors.email && <div className={styles.error}>{forgotErrors.email}</div>}
                                    </div>

                                    <div className={styles.modalFooter}>
                                        <button
                                            type="button"
                                            className={styles.cancelModalBtn}
                                            onClick={handleCloseForgotModal}
                                            disabled={forgotSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className={styles.submitBtn}
                                            style={{ marginTop: 0 }}
                                            disabled={forgotSubmitting}
                                        >
                                            {forgotSubmitting ? "Sending Code..." : "Send Verification Code"}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Step 2: Verification Code & New Password */}
                            {forgotStep === 2 && (
                                <form onSubmit={handleResetPassword} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                                    <div className={styles.resendWrap}>
                                        <span>Didn’t get the email?</span>
                                        <button
                                            type="button"
                                            className={styles.resendBtn}
                                            onClick={handleSendOtp}
                                            disabled={resendCooldown > 0 || forgotSubmitting}
                                        >
                                            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend Code"}
                                        </button>
                                    </div>

                                    <div className={styles.fieldGroup}>
                                        <label className={styles.label}>
                                            6-Digit Verification Code <span className={styles.required}>*</span>
                                        </label>
                                        <div className={`${styles.inputWrap} ${forgotErrors.otp ? styles.inputError : ""}`}>
                                            <span className={styles.icon}><FaKey /></span>
                                            <input
                                                type="text"
                                                name="otp"
                                                maxLength="6"
                                                value={forgotData.otp}
                                                onChange={handleForgotChange}
                                                placeholder="e.g. 123456"
                                                className={styles.input}
                                                style={{ letterSpacing: "3px", fontWeight: "700" }}
                                                autoFocus
                                            />
                                        </div>
                                        {forgotErrors.otp && <div className={styles.error}>{forgotErrors.otp}</div>}
                                    </div>

                                    <div className={styles.fieldGroup}>
                                        <label className={styles.label}>
                                            New Password <span className={styles.required}>*</span>
                                        </label>
                                        <div className={`${styles.inputWrap} ${forgotErrors.newPassword ? styles.inputError : ""}`}>
                                            <span className={styles.icon}><FaLock /></span>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                name="newPassword"
                                                value={forgotData.newPassword}
                                                onChange={handleForgotChange}
                                                placeholder="Minimum 6 characters"
                                                className={styles.input}
                                                autoComplete="new-password"
                                            />
                                            <button
                                                type="button"
                                                className={styles.toggleEye}
                                                onClick={() => setShowNewPassword((p) => !p)}
                                                aria-label="Toggle password visibility"
                                            >
                                                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {forgotErrors.newPassword && <div className={styles.error}>{forgotErrors.newPassword}</div>}
                                    </div>

                                    <div className={styles.fieldGroup}>
                                        <label className={styles.label}>
                                            Confirm New Password <span className={styles.required}>*</span>
                                        </label>
                                        <div className={`${styles.inputWrap} ${forgotErrors.confirmPassword ? styles.inputError : ""}`}>
                                            <span className={styles.icon}><FaLock /></span>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                value={forgotData.confirmPassword}
                                                onChange={handleForgotChange}
                                                placeholder="Re-enter new password"
                                                className={styles.input}
                                                autoComplete="new-password"
                                            />
                                            <button
                                                type="button"
                                                className={styles.toggleEye}
                                                onClick={() => setShowConfirmPassword((p) => !p)}
                                                aria-label="Toggle password visibility"
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {forgotErrors.confirmPassword && <div className={styles.error}>{forgotErrors.confirmPassword}</div>}
                                    </div>

                                    <div className={styles.modalFooter}>
                                        <button
                                            type="button"
                                            className={styles.cancelModalBtn}
                                            onClick={() => { setForgotStep(1); setForgotAlert(null); }}
                                            disabled={forgotSubmitting}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            className={styles.submitBtn}
                                            style={{ marginTop: 0 }}
                                            disabled={forgotSubmitting}
                                        >
                                            {forgotSubmitting ? "Resetting..." : "Reset Password"}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Step 3: Success View */}
                            {forgotStep === 3 && (
                                <div className={styles.successBox}>
                                    <div className={styles.successIconLarge}>
                                        <FaCheckCircle />
                                    </div>
                                    <h4 className={styles.successHeading}>Password Reset Successfully!</h4>
                                    <p className={styles.successText}>
                                        Your password has been updated. You can now sign in using your new credentials.
                                    </p>
                                    <button
                                        type="button"
                                        className={styles.submitBtn}
                                        style={{ width: "100%", marginTop: 0 }}
                                        onClick={handleCloseForgotModal}
                                    >
                                        Proceed to Sign In
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginPage;
